// Room-layout geometry for the "Detect room" preview. Pure functions, no viewer.
//
// A layout is the image `layout_json` format the floor editor already reads
// (pannellum-admin.js `_addPoints`): `uv` lists, per wall corner, its ceiling
// point then its floor point, as equirectangular 0..1 coordinates. Yaw/pitch
// use the editor's conversion (FloorplanManager.xToYaw / yToPitch).

const DEG = 180 / Math.PI;
const RAD = Math.PI / 180;

export const uToYaw = (u) => u * 360 - 180;
export const vToPitch = (v) => (v - 0.5) * -180;

// Corners as {yaw, floorPitch, ceilPitch} in degrees, in the model's order.
export function layoutCorners(layout) {
  const uv = (layout && layout.uv) || [];
  const corners = [];
  for (let i = 0; i + 1 < uv.length; i += 2) {
    const [cu, cv] = uv[i];
    const [fu, fv] = uv[i + 1];
    corners.push({ yaw: uToYaw((cu + fu) / 2), ceilPitch: vToPitch(cv), floorPitch: vToPitch(fv) });
  }
  return corners;
}

// Floor position of a corner seen at (yaw, floorPitch) from a camera 1 unit
// above the floor; null for a point at or above the horizon.
function floorXZ(yaw, floorPitch) {
  if (floorPitch >= -0.5) return null;
  const d = 1 / Math.tan(-floorPitch * RAD);
  return [d * Math.sin(yaw * RAD), d * Math.cos(yaw * RAD)];
}

// Ceiling height (camera height = 1) from the corners' ceiling pitches — one
// value for the room, the median, so one odd corner can't tilt the ceiling.
export function ceilingHeight(corners) {
  const hs = corners
    .map(c => {
      const xz = floorXZ(c.yaw, c.floorPitch);
      if (!xz || c.ceilPitch <= 0.5) return null;
      return Math.hypot(xz[0], xz[1]) * Math.tan(c.ceilPitch * RAD);
    })
    .filter(h => h !== null && Number.isFinite(h))
    .sort((a, b) => a - b);
  if (!hs.length) return null;
  return hs[Math.floor(hs.length / 2)];
}

// Floor and ceiling lines as [yaw°, pitch°] polylines that follow the walls.
// A straight wall is a curve on the panorama, so each wall is interpolated on
// the floor plane (`steps` points per wall) and turned back into yaw/pitch.
export function wallLines(layout, steps = 24) {
  const corners = layoutCorners(layout);
  const pts = corners.map(c => floorXZ(c.yaw, c.floorPitch));
  if (corners.length < 3 || pts.some(p => !p)) return { floor: [], ceiling: [], corners };
  const h = ceilingHeight(corners);
  const floor = [];
  const ceiling = [];
  for (let i = 0; i < pts.length; i++) {
    const [ax, az] = pts[i];
    const [bx, bz] = pts[(i + 1) % pts.length];
    for (let s = 0; s < steps; s++) {
      const t = s / steps;
      const x = ax + (bx - ax) * t;
      const z = az + (bz - az) * t;
      const d = Math.hypot(x, z);
      const yaw = Math.atan2(x, z) * DEG;
      floor.push([yaw, -Math.atan(1 / d) * DEG]);
      if (h) ceiling.push([yaw, Math.atan(h / d) * DEG]);
    }
  }
  floor.push(floor[0]);
  if (ceiling.length) ceiling.push(ceiling[0]);
  return { floor, ceiling, corners };
}

// Short human summary for the panel: "6 corners · 0.6 s".
export function describeResult(result) {
  if (!result) return '';
  if (result.error) return `failed: ${result.error}`;
  const n = Math.floor(((result.uv && result.uv.length) || 0) / 2);
  const secs = result.ms != null ? ` · ${(result.ms / 1000).toFixed(1)} s` : '';
  return `${n} corners${secs}`;
}
